import { hasRealm } from "../match/cards";
import type { Navigator, Screen } from "../app/screen";
import { pageShell } from "../ui/page";
import { REALM_COLOUR } from "../card-frames.generated";
import {
  LORE_FEU_FOLLET,
  LORE_FIGURE,
  LORE_OPENING,
  LORE_STRANDS,
  REALM_LINES,
} from "../lore.content";

/**
 * THE LONGWAKE - what this place is.
 *
 * ⭐ **A CARD GAME WITH NO SETTING IS 171 PICTURES.** Revival's whole differentiator is that it is
 * North American folklore rather than another western, and until now that existed only in a design
 * document and in card art a player has to infer it from. This is the one screen that says it.
 *
 * ⭐ **THE WORDS ARE IN `lore.content.ts`, NOT HERE.** The voice-over will need the same text, and a
 * narrated version that disagrees with the written one is a defect invisible in either half.
 *
 * ⭐ **THE REALMS ARE ITERATED FROM THE GENERATED COLOUR MAP, NEVER LISTED HERE.** `REALM_COLOUR` is
 * produced from `RevivalTheme.cs`, so the set of realms and their accents have one owner. A realm
 * added to the game appears on this page without anyone remembering to add it - and appears in its
 * own colour, because that colour is the same value its cards are framed in.
 *
 * ⚠️ **`Neutral` IS NOT A REALM.** It is the allegiance a card has when it owes none, and listing it
 * beside the five would teach a player something false on the screen that exists to teach them the
 * setting.
 */
export const LoreScreen: Screen = {
  id: "lore",

  mustOffer: ["BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "THE LONGWAKE", "what this place is");
    host.append(shell.el);

    const root = document.createElement("div");
    root.className = "lore";
    shell.body.append(root);

    // ── the opening ──────────────────────────────────────────────────────────────────────────────
    const open = document.createElement("section");
    open.className = "lore__open";
    for (const p of LORE_OPENING) {
      const el = document.createElement("p");
      // ⚠️ The FIRST line carries the whole idea, so it is the one that gets the display face.
      el.className = p === LORE_OPENING[0] ? "lore__lede" : "lore__body";
      el.textContent = p;
      open.append(el);
    }
    root.append(open);

    // ── the strands ──────────────────────────────────────────────────────────────────────────────
    root.append(heading("WHAT CAME TOGETHER"));

    const strands = document.createElement("div");
    strands.className = "lore__strands";
    for (const s of LORE_STRANDS) {
      const card = document.createElement("article");
      card.className = "lore__strand";

      const name = document.createElement("h3");
      name.className = "lore__strandname";
      name.textContent = s.name;

      const body = document.createElement("p");
      body.className = "lore__body";
      body.textContent = s.body;

      const tex = document.createElement("span");
      tex.className = "lore__texture";
      tex.textContent = s.texture;

      card.append(name, body, tex);
      strands.append(card);
    }
    root.append(strands);

    // ── the two set-pieces ───────────────────────────────────────────────────────────────────────
    root.append(heading("THE THING THAT PROVES IT"));
    root.append(feature(LORE_FEU_FOLLET.title, LORE_FEU_FOLLET.body));
    root.append(feature(LORE_FIGURE.title, LORE_FIGURE.body));

    // ── the realms ───────────────────────────────────────────────────────────────────────────────
    root.append(heading("THE FIVE REALMS"));

    const realms = document.createElement("div");
    realms.className = "lore__realms";
    for (const id of Object.keys(REALM_COLOUR)) {
      if (!hasRealm(id)) continue;
      const row = document.createElement("div");
      row.className = "lore__realm";
      // The realm's own accent, from the same table that frames its cards.
      row.style.setProperty("--realm", REALM_COLOUR[id] ?? "var(--muted)");

      const name = document.createElement("span");
      name.className = "lore__realmname";
      // ⚠️ Derived from the ID, exactly as the board generator does it. A hand-written label table
      // would be a second owner of a name the engine already decides.
      name.textContent = id.replace(/_/g, " ");

      const line = document.createElement("span");
      line.className = "lore__realmline";
      line.textContent = REALM_LINES[id] ?? "";

      row.append(name, line);
      realms.append(row);
    }
    root.append(realms);
  },
};

function heading(text: string): HTMLElement {
  const h = document.createElement("h2");
  h.className = "lore__heading";
  h.textContent = text;
  return h;
}

function feature(title: string, body: string): HTMLElement {
  const el = document.createElement("section");
  el.className = "lore__feature";
  const h = document.createElement("h3");
  h.className = "lore__featuretitle";
  h.textContent = title;
  const p = document.createElement("p");
  p.className = "lore__body";
  p.textContent = body;
  el.append(h, p);
  return el;
}
